import { SeedError, UnexpectedError } from './errors';

export const requestMethods = {
  GET: 'get',
  POST: 'post',
  PATCH: 'patch',
  PUT: 'put',
  DELETE: 'delete',
};

export interface RequestOptions {
  /** Lets the request finish after the page navigates away. */
  keepalive?: boolean;
  /** Set to false to get the raw response back on 4xx/5xx instead of a thrown error. */
  throwOnError?: boolean;
}

function urlForPath(path) {
  if (typeof window === 'undefined') {
    return `${process.env.SEED_CORE_URI || process.env.NEXT_PUBLIC_API_PROXY || ''}${path}`;
  }
  return `${process.env.NEXT_PUBLIC_API_PROXY || ''}${path}`;
}

/**
 * Unauthenticated request to the Seed Core API. Throws `SeedError` or
 * `UnexpectedError` on a 4xx/5xx unless `throwOnError` is false.
 * @param {string} path Must include leading slash.
 */
export async function request(
  method,
  path,
  body = undefined,
  { keepalive, throwOnError = true }: RequestOptions = {},
) {
  const url = urlForPath(path);
  const headers = {
    'Content-Type': 'application/json',
  };
  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers,
    body: JSON.stringify(body),
    ...(keepalive ? { keepalive: true } : {}),
  });
  if (throwOnError && response.status > 399) {
    if (response.headers.get('x-seed-error')) {
      const responseBody = await response.json();
      throw new SeedError(
        responseBody.code,
        responseBody.detail,
        response.status,
      );
    }
    throw new UnexpectedError(
      `Request failed with status ${response.status}`,
      response,
    );
  }
  return response;
}

export async function getRequest(path, options?: RequestOptions) {
  return request(requestMethods.GET, path, undefined, options);
}

export async function postRequest(path, body, options?: RequestOptions) {
  return request(requestMethods.POST, path, body, options);
}

export async function putRequest(path, body, options?: RequestOptions) {
  return request(requestMethods.PUT, path, body, options);
}
